import * as React from 'react';
import { createPortal } from 'react-dom';
import { NO_HIGHLIGHTED_OPTION_INDEX } from './hooks/selectState.utils';
import { useGroupedSelectKeyboardNavigation } from './hooks/useGroupedSelectKeyboardNavigation';
import { useSelectAccessibilityIds } from './hooks/useSelectAccessibilityIds';
import { useSelectMenuPosition } from './hooks/useSelectMenuPosition';
import { useSelectOutsideClick } from './hooks/useSelectOutsideClick';
import { useSelectSearch } from './hooks/useSelectSearch';
import { useSelectState } from './hooks/useSelectState';
import { SelectOptionsList } from './internal/SelectOptionsList';
import { SelectTrigger } from './internal/SelectTrigger';
import type {
	IOption,
	ISelectBaseProps,
	ISelectGroupRenderModel,
	SelectOptionValue,
	SelectSelectionMode,
} from './types';
import { CaptionTypography } from '@components/atoms';
import { cn } from '@utils';

const DEFAULT_SELECTION_MODE: SelectSelectionMode = 'single';
const SELECT_NAVIGATION_KEYS = new Set(['ArrowDown', 'ArrowUp', 'End', 'Escape', 'Home', 'Tab']);
const SELECT_OPEN_KEYS = new Set(['Enter', ' ']);
const ENABLED_GROUP_SELECT_ALL_SELECTOR = '[data-select-group-all]:not(:disabled)';
const SCROLL_ALIGNMENT_OPTIONS: ScrollIntoViewOptions = {
	block: 'nearest',
	inline: 'nearest',
};

const combineDescribedByIds = (...ids: ReadonlyArray<string | undefined>): string | undefined =>
	ids.filter((id): id is string => Boolean(id)).join(' ') || undefined;

const isSelectNavigationKey = (key: string): boolean => SELECT_NAVIGATION_KEYS.has(key);
const FOCUSABLE_OUTSIDE_TARGET_SELECTOR = [
	'a[href]',
	'button:not([disabled])',
	'input:not([disabled])',
	'select:not([disabled])',
	'textarea:not([disabled])',
	'[contenteditable="true"]',
	'[tabindex]:not([tabindex="-1"])',
].join(',');

const getElementFromEventTarget = (target: EventTarget | null): Element | null => {
	if (target instanceof Element) {
		return target;
	}
	if (target instanceof Node) {
		return target.parentElement;
	}
	return null;
};

const getLabelControlFromEventTarget = (targetElement: Element | null): HTMLElement | null => {
	const labelElement = targetElement?.closest('label');
	return labelElement instanceof HTMLLabelElement ? labelElement.control : null;
};

const isFocusableOutsideTarget = (target: EventTarget | null): boolean => {
	const targetElement = getElementFromEventTarget(target);
	const labelControl = getLabelControlFromEventTarget(targetElement);
	return Boolean(
		targetElement?.closest(FOCUSABLE_OUTSIDE_TARGET_SELECTOR) ||
			labelControl?.matches(FOCUSABLE_OUTSIDE_TARGET_SELECTOR)
	);
};

/**
 * Provides the API-agnostic rendering and interaction foundation for select wrappers.
 */
export const SelectBase = <TValue extends SelectOptionValue = SelectOptionValue, TMetadata = unknown>({
	'aria-describedby': ariaDescribedBy,
	'aria-label': ariaLabel,
	'aria-labelledby': ariaLabelledBy,
	'aria-required': ariaRequired,
	className,
	'data-testid': dataTestId = 'select-base',
	defaultOpen = false,
	defaultSearchValue = '',
	disabled = false,
	emptyLabel,
	enableGroupSelectAll = false,
	error,
	filterOption,
	getOptionKey,
	groupedOptions,
	getGroupSelectAllLabel,
	id,
	isOpen: controlledIsOpen,
	lastOptionRef,
	loading = false,
	loadingMore = false,
	loadingLabel,
	name,
	onChange,
	onGroupSelect,
	onOpenChange,
	onSearchChange,
	options,
	placeholder,
	renderOption,
	renderValue,
	searchDebounceMs,
	searchPlaceholder,
	searchValue: controlledSearchValue,
	searchable = false,
	selectionMode = DEFAULT_SELECTION_MODE,
	value,
	...props
}: ISelectBaseProps<TValue, TMetadata>): React.ReactElement => {
	const menuRef = React.useRef<HTMLDivElement>(null);
	const rootRef = React.useRef<HTMLDivElement>(null);
	const searchInputRef = React.useRef<HTMLInputElement>(null);
	const triggerRef = React.useRef<HTMLButtonElement>(null);
	const restoreFocusOnCloseRef = React.useRef(true);
	const focusFirstGroupSelectAllOnOpenRef = React.useRef(false);
	const tabCloseTimeoutRef = React.useRef<number | undefined>(undefined);
	const [highlightedGroupSelectAllId, setHighlightedGroupSelectAllId] = React.useState<string>();
	const { errorId, getGroupSelectAllId, getOptionId, listboxId, selectId, triggerId } =
		useSelectAccessibilityIds({ getOptionKey, id });

	const { clearSearch, filteredOptions, searchValue, setSearchValue } = useSelectSearch({
		debounceMs: searchDebounceMs,
		defaultSearchValue,
		filterOption,
		onSearchChange,
		options,
		searchValue: controlledSearchValue,
	});
	const displayedOptions = searchable ? filteredOptions : options;
	const interactiveOptions = loading ? [] : displayedOptions;
	const {
		clearHighlightedOption,
		closeSelect,
		handleKeyDown,
		hideHighlight,
		highlightFirstOption,
		highlightedIndex,
		highlightedOption,
		isHighlightVisible,
		isOpen,
		openSelect,
		selectOption,
		setHighlightedIndex,
		showHighlight,
		toggleSelect,
	} = useSelectState({
		defaultOpen,
		disabled,
		isOpen: controlledIsOpen,
		loading,
		onChange,
		onOpenChange,
		options: interactiveOptions,
		selectionMode,
		value,
	});
	const selectedOptions = React.useMemo(
		() => options.filter(option => value.includes(option.value)),
		[options, value]
	);
	const hasSearchValue = searchable && searchValue.trim().length > 0;
	const previousIsOpenRef = React.useRef(false);
	const previousSearchValueRef = React.useRef(searchValue);
	const triggerDescribedBy = combineDescribedByIds(ariaDescribedBy, error ? errorId : undefined);
	const activeDescendantId =
		isOpen && !loading
			? highlightedGroupSelectAllId ??
				(highlightedIndex >= 0 && highlightedOption
					? getOptionId(highlightedOption)
					: undefined)
			: undefined;
	const triggerActiveDescendantId = searchable ? undefined : activeDescendantId;
	const triggerControlsId = searchable ? undefined : listboxId;
	const triggerExpanded = searchable ? undefined : isOpen;
	const searchInputId = searchable && isOpen ? selectId : undefined;
	const triggerElementId = searchable && isOpen ? triggerId : selectId;
	const triggerRole = searchable ? undefined : 'combobox';
	const triggerTabIndex = searchable && isOpen ? -1 : undefined;
	const { openAbove, portalContainer, style: menuStyle } = useSelectMenuPosition({
		isOpen,
		menuRef,
		triggerRef,
	});
	const keyboardFocusTargetRef = searchable ? searchInputRef : triggerRef;
	const hasGroupSelectAll =
		groupedOptions !== undefined &&
		enableGroupSelectAll &&
		selectionMode === 'multiple' &&
		Boolean(onGroupSelect) &&
		Boolean(getGroupSelectAllLabel);
	const handleGroupedKeyboardNavigation = useGroupedSelectKeyboardNavigation({
		clearHighlightedOption,
		focusTargetRef: keyboardFocusTargetRef,
		hideHighlight,
		highlightedGroupSelectAllId,
		highlightedIndex,
		menuRef,
		preserveFocus: searchable,
		setHighlightedGroupSelectAllId,
		setHighlightedIndex,
		showHighlight,
	});
	const handleSearchChange = React.useCallback(
		(nextSearchValue: string) => {
			setHighlightedGroupSelectAllId(undefined);
			setSearchValue(nextSearchValue);
		},
		[setSearchValue]
	);

	const handleOutsideClick = React.useCallback((event: MouseEvent) => {
		restoreFocusOnCloseRef.current = !isFocusableOutsideTarget(event.target);
		closeSelect();
	}, [closeSelect]);
	useSelectOutsideClick({ isOpen, menuRef, onOutsideClick: handleOutsideClick, rootRef });

	React.useEffect(() => {
		if (!previousIsOpenRef.current && isOpen && searchable) {
			if (!searchInputRef.current) {
				return;
			}
			searchInputRef.current.focus();
		}
		if (previousIsOpenRef.current && !isOpen) {
			setHighlightedGroupSelectAllId(undefined);
			if (searchable) {
				clearSearch();
			}
			if (restoreFocusOnCloseRef.current) {
				triggerRef.current?.focus();
			}
			restoreFocusOnCloseRef.current = true;
		}
		previousIsOpenRef.current = isOpen;
	}, [clearSearch, isOpen, portalContainer, searchable]);

	React.useEffect(() => {
		if (!isOpen || !focusFirstGroupSelectAllOnOpenRef.current) {
			return;
		}

		const firstGroupSelectAll = menuRef.current?.querySelector(ENABLED_GROUP_SELECT_ALL_SELECTOR);
		if (firstGroupSelectAll instanceof HTMLButtonElement) {
			focusFirstGroupSelectAllOnOpenRef.current = false;
			hideHighlight();
			clearHighlightedOption();
			setHighlightedGroupSelectAllId(firstGroupSelectAll.id || undefined);
			if (searchable) {
				searchInputRef.current?.focus();
			} else {
				firstGroupSelectAll.focus();
			}
			firstGroupSelectAll.scrollIntoView(SCROLL_ALIGNMENT_OPTIONS);
		}
	}, [clearHighlightedOption, hideHighlight, isOpen, portalContainer, searchable]);

	React.useEffect(() => {
		const previousSearchQuery = previousSearchValueRef.current.trim();
		const searchQuery = searchValue.trim();
		const hasActiveSearchChanged =
			isOpen && searchable && previousSearchQuery !== searchQuery && searchQuery.length > 0;
		if (hasActiveSearchChanged) {
			if (highlightedIndex === NO_HIGHLIGHTED_OPTION_INDEX) {
				highlightFirstOption();
			}
			showHighlight();
		}
		previousSearchValueRef.current = searchValue;
	}, [highlightFirstOption, highlightedIndex, isOpen, searchValue, searchable, showHighlight]);

	React.useEffect(
		() => () => {
			if (tabCloseTimeoutRef.current !== undefined) {
				window.clearTimeout(tabCloseTimeoutRef.current);
			}
		},
		[]
	);

	const handleOptionSelect = React.useCallback(
		(option: IOption<TValue, TMetadata>) => {
			restoreFocusOnCloseRef.current = true;
			setHighlightedGroupSelectAllId(undefined);
			selectOption(option);
		},
		[selectOption]
	);

	const handleGroupSelect = React.useCallback(
		(group: ISelectGroupRenderModel<TValue, TMetadata>) => {
			setHighlightedGroupSelectAllId(undefined);
			clearHighlightedOption();
			hideHighlight();
			onGroupSelect?.(group);
		},
		[clearHighlightedOption, hideHighlight, onGroupSelect]
	);

	const handleRootKeyDown = React.useCallback(
		(event: React.KeyboardEvent<HTMLElement>) => {
			const eventTarget = event.target;
			const isNonOptionButton =
				eventTarget instanceof HTMLButtonElement &&
				eventTarget !== triggerRef.current &&
				eventTarget.getAttribute('role') !== 'option';
			if (isNonOptionButton && !isSelectNavigationKey(event.key)) {
				return;
			}
			if (
				eventTarget instanceof HTMLInputElement &&
				(event.key === ' ' || event.key === 'Home' || event.key === 'End')
			) {
				return;
			}
			if (eventTarget instanceof HTMLInputElement && event.key === 'Tab' && isOpen) {
				restoreFocusOnCloseRef.current = false;
				if (tabCloseTimeoutRef.current !== undefined) {
					window.clearTimeout(tabCloseTimeoutRef.current);
				}
				tabCloseTimeoutRef.current = window.setTimeout(() => {
					tabCloseTimeoutRef.current = undefined;
					closeSelect();
				}, 0);
				return;
			}
			if (
				!isOpen &&
				SELECT_OPEN_KEYS.has(event.key) &&
				eventTarget === triggerRef.current &&
				hasGroupSelectAll
			) {
				event.preventDefault();
				openSelect('pointer');
				return;
			}
			if (
				!isOpen &&
				event.key === 'ArrowDown' &&
				eventTarget === triggerRef.current &&
				enableGroupSelectAll &&
				selectionMode === 'multiple'
			) {
				focusFirstGroupSelectAllOnOpenRef.current = true;
				restoreFocusOnCloseRef.current = true;
				handleKeyDown(event);
				return;
			}
			if (hasGroupSelectAll && handleGroupedKeyboardNavigation(event)) {
				return;
			}

			restoreFocusOnCloseRef.current = event.key !== 'Tab';
			handleKeyDown(event);
		},
		[
			closeSelect,
			enableGroupSelectAll,
			handleGroupedKeyboardNavigation,
			handleKeyDown,
			hasGroupSelectAll,
			isOpen,
			openSelect,
			selectionMode,
		]
	);

	return (
		<div
			{...props}
			className={cn('relative w-full', className)}
			data-name={name}
			data-testid={dataTestId}
			ref={rootRef}
		>
			<SelectTrigger
				aria-activedescendant={triggerActiveDescendantId}
				aria-autocomplete={searchable ? undefined : 'none'}
				aria-controls={triggerControlsId}
				aria-describedby={triggerDescribedBy}
				aria-expanded={triggerExpanded}
				aria-haspopup="listbox"
				aria-invalid={Boolean(error)}
				aria-label={ariaLabel}
				aria-labelledby={ariaLabelledBy}
				aria-required={ariaRequired}
				disabled={disabled}
				error={error}
				id={triggerElementId}
				onClick={toggleSelect}
				onKeyDown={handleRootKeyDown}
				open={isOpen}
				placeholder={placeholder}
				renderValue={renderValue}
				role={triggerRole}
				selectedOptions={selectedOptions}
				tabIndex={triggerTabIndex}
				triggerRef={triggerRef}
			/>
			{isOpen &&
        portalContainer &&
        createPortal(
          <SelectOptionsList
            activeDescendantId={searchable ? activeDescendantId : undefined}
            className={cn('mt-0', openAbove && '-translate-y-full')}
            containerRef={menuRef}
            disableGroupSelectAll={hasSearchValue}
            emptyLabel={emptyLabel}
            enableGroupSelectAll={enableGroupSelectAll}
            getGroupSelectAllId={getGroupSelectAllId}
            getGroupSelectAllLabel={getGroupSelectAllLabel}
            getOptionId={getOptionId}
            getOptionKey={getOptionKey}
            groupedOptions={groupedOptions}
            highlightedGroupSelectAllId={highlightedGroupSelectAllId}
            highlightedIndex={highlightedIndex}
            isHighlightVisible={isHighlightVisible}
            listboxId={listboxId}
            listboxLabelledBy={searchInputId ?? triggerElementId}
            loading={loading}
            loadingLabel={loadingLabel}
            loadingMore={loadingMore}
            lastOptionRef={lastOptionRef}
            onGroupSelect={onGroupSelect ? handleGroupSelect : undefined}
            onKeyDown={handleRootKeyDown}
            onOptionSelect={handleOptionSelect}
            onSearchChange={handleSearchChange}
            options={displayedOptions}
            renderOption={renderOption}
            searchable={searchable}
            searchInputAriaDescribedBy={triggerDescribedBy}
            searchInputAriaInvalid={Boolean(error)}
            searchInputAriaLabel={ariaLabel}
            searchInputAriaLabelledBy={ariaLabelledBy}
            searchInputAriaRequired={ariaRequired}
            searchInputHasVisibleLabel={Boolean(id && !ariaLabel && !ariaLabelledBy)}
            searchInputId={searchInputId}
            searchInputRef={searchInputRef}
            searchPlaceholder={searchPlaceholder}
            searchValue={searchValue}
            selectedValues={value}
            selectionMode={selectionMode}
            style={menuStyle}
          />,
          portalContainer
        )}
			{error && (
				<div className="mt-1" data-testid="select-error" id={errorId} role="alert">
					<CaptionTypography className="text-error">{error}</CaptionTypography>
				</div>
			)}
		</div>
	);
};
